import { alternateLinks, rootCanonicalUrl } from "@/config/canonical";
import { GAME_NAME } from "@/config/game";
import { Switch } from "@/design-system/Switch/Switch";
import { mixpanelTrack } from "@/events/mixpanelTrack";
import { trackUpgrade } from "@/events/subscriptionMixpanel";
import { cn } from "@/lib/utils";
import {
  backendErrorCode,
  errorMessage,
  useTranslation,
} from "@repo/shared/i18n";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Oval } from "react-loading-icons";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import * as privateApi from "../../api/privateApi";
import PremiumFeatures from "../../components/organisms/PremiumFeatures/PremiumFeatures";
import SubscriptionCard from "../../components/organisms/SubscriptionCard/SubscriptionCard";
import { addAlert, showModal } from "../../redux/message/messageSlice";
import { AppDispatch } from "../../redux/store";
import {
  getUserSelector,
  hasSubSelector,
  userCurrencySelector,
  userPricesSelector,
  userSubscriptionSelector,
} from "../../redux/user/userSlice";
import { setBuyIntent } from "../../redux/user/userSlice";
import { BREAKPOINTS } from "../../settings";
import { CreditsExplainer } from "./CreditsExplainer";
import "./Subscription.css";

type LinkState = {
  from: string;
};

/*
  The subscription page as it was before the credits model: three tiers, a
  tick/cross matrix, and a pitch built around what each plan unlocks. Kept
  verbatim for legacy subscribers on a still-active old tier — the rename is
  the only edit.
*/
const LegacySubscriptionPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation("subscription");
  const { user } = useSelector(getUserSelector);
  const subscription = user.subscription;
  const isLoggedIn = !!user.uuid;
  const hasUltimateSub = subscription === "diamond";
  const hasEliteSub = subscription === "gold";
  const hasProSub = subscription === "silver";
  const location = useLocation();
  const { state } = location;
  const from = (state as LinkState)?.from;
  const clubImportRef = useRef(null);
  const creditsRef = useRef(null);
  const plansRef = useRef<HTMLDivElement>(null);
  const [yearlySubActive, setYearlySubActive] = useState(true);
  const hasSub = useSelector(hasSubSelector);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const prices = useSelector(userPricesSelector);
  const currency = useSelector(userCurrencySelector);
  const [priceId, setPriceId] = useState<string>("");
  const [loading, setLoading] = useState(false);

  /*
    Subscribers keep the page exactly as it was — they are on a tier that isn't
    metered, so credits are not part of their model of the product and naming
    them here would only raise questions. Everyone else gets the credits
    explainer in place of the club-import pitch.

    Read through userSubscriptionSelector rather than hasSubSelector: only the
    former falls back to the cold-start cache, so a subscriber reloading the page
    doesn't flash the credits section before users/profile resolves.
  */
  const cachedSubscription = useSelector(userSubscriptionSelector);
  const hasActiveSub =
    cachedSubscription === "silver" ||
    cachedSubscription === "gold" ||
    cachedSubscription === "diamond";

  useEffect(() => {
    mixpanelTrack("landed_on_subscription_page", { currency });
    // Either target can be absent depending on the branch above, and `from`
    // arrives from whatever linked here — so never assume the ref is mounted.
    if (from === "import") {
      clubImportRef.current?.scrollIntoView();
    } else if (from === "credits") {
      creditsRef.current?.scrollIntoView();
    } else {
      window.scrollTo(0, 0);
    }
  }, [from]);

  const [selectedSubscription, setSelectedSubscription] = useState<
    "Elite" | "Pro" | "Ultimate"
  >("Ultimate");

  const handleSubscriptionClicked = (sub: "Elite" | "Pro" | "Ultimate") => {
    setSelectedSubscription(sub);
  };

  useEffect(() => {
    switch (selectedSubscription) {
      case "Pro":
        setPriceId(yearlySubActive ? proYearlyPrice?.id : proPrice?.id);
        break;
      case "Elite":
        setPriceId(yearlySubActive ? eliteYearlyPrice?.id : elitePrice?.id);
        break;
      case "Ultimate":
        setPriceId(
          yearlySubActive ? ultimateYearlyPrice?.id : ultimatePrice?.id,
        );
    }
  }, [selectedSubscription, prices, yearlySubActive]);

  const getSubscriptionClicked = async () => {
    trackUpgrade("Hero");
    if (isLoggedIn) {
      setLoading(true);
      try {
        if (hasSub) {
          await privateApi.createCustomerPortalSession();
        } else {
          mixpanelTrack("goes_to_checkout", {
            currency,
            subscription: selectedSubscription,
            subscriptionInterval: yearlySubActive ? "yearly" : "monthly",
          });
          await privateApi.createCheckoutSession({
            priceId,
            freeTrial: 0,
          });
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
        dispatch(
          addAlert({
            description: errorMessage(backendErrorCode(error)),
            type: "error",
          }),
        );
      }
    } else {
      mixpanelTrack("goes_to_checkout", {
        currency,
        subscription: selectedSubscription,
        subscriptionInterval: yearlySubActive ? "yearly" : "monthly",
      });
      dispatch(showModal({ modalType: "sign-in-to-subscribe" }));
      dispatch(setBuyIntent({ subscriptionId: priceId }));
    }
  };

  const proPrice = prices.find(
    (price) => price.subscription === "silver" && price.interval === "monthly",
  );
  const elitePrice = prices.find(
    (price) => price.subscription === "gold" && price.interval === "monthly",
  );
  const ultimatePrice = prices.find(
    (price) => price.subscription === "diamond" && price.interval === "monthly",
  );
  const proYearlyPrice = prices.find(
    (price) => price.subscription === "silver" && price.interval === "yearly",
  );
  const eliteYearlyPrice = prices.find(
    (price) => price.subscription === "gold" && price.interval === "yearly",
  );
  const ultimateYearlyPrice = prices.find(
    (price) => price.subscription === "diamond" && price.interval === "yearly",
  );

  const subscriptions = (
    <div
      ref={plansRef}
      className={
        "flex touch-device:flex-row justify-center gap-x-16 text-left relative"
      }
    >
      {(!isMobile || selectedSubscription === "Pro") && (
        <SubscriptionCard
          currency={currency}
          selected={selectedSubscription === "Pro"}
          activeSubscription={hasProSub}
          onClick={handleSubscriptionClicked}
          monthlyPrice={
            yearlySubActive
              ? proYearlyPrice?.monthlyPrice
              : proPrice?.monthlyPrice
          }
          yearlyPrice={proYearlyPrice?.yearlyPrice}
          tier={"Pro"}
          {...(yearlySubActive
            ? { annualDiscount: proYearlyPrice?.savings }
            : {})}
        />
      )}
      {(!isMobile || selectedSubscription === "Ultimate") && (
        <>
          <SubscriptionCard
            currency={currency}
            selected={selectedSubscription === "Ultimate"}
            activeSubscription={hasUltimateSub}
            onClick={handleSubscriptionClicked}
            monthlyPrice={
              yearlySubActive
                ? ultimateYearlyPrice?.monthlyPrice
                : ultimatePrice?.monthlyPrice
            }
            yearlyPrice={ultimateYearlyPrice?.yearlyPrice}
            tier={"Ultimate"}
            {...(yearlySubActive
              ? { annualDiscount: ultimateYearlyPrice?.savings }
              : {})}
            mostPopular={true}
          />
        </>
      )}

      {(!isMobile || selectedSubscription === "Elite") && (
        <SubscriptionCard
          currency={currency}
          selected={selectedSubscription === "Elite"}
          activeSubscription={hasEliteSub}
          onClick={handleSubscriptionClicked}
          monthlyPrice={
            yearlySubActive
              ? eliteYearlyPrice?.monthlyPrice
              : elitePrice?.monthlyPrice
          }
          yearlyPrice={eliteYearlyPrice?.yearlyPrice}
          tier={"Elite"}
          {...(yearlySubActive
            ? { annualDiscount: eliteYearlyPrice?.savings }
            : {})}
        />
      )}
    </div>
  );

  const HugeButton = ({ children }: { children?: React.ReactNode }) => (
    <button
      className="relative text-lg touch-device:text-2xl text-white bg-primary-blue touch-device:hover:bg-primary-blue/90 w-[263px] touch-device:w-[333px] rounded-lg mx-auto py-[9.6px] font-semibold large-2:mt-8 touch-device:mt-4"
      style={{
        boxShadow:
          "0px -1.8px 0px 0px rgba(47, 43, 67, 0.30) inset, 0px 2.4px 4.8px 0px rgba(0, 0, 0, 0.25)",
      }}
      onClick={(e) => {
        e.stopPropagation();
        getSubscriptionClicked();
      }}
    >
      {children}
    </button>
  );

  const MobileSelectSubscriptionButton = ({
    children,
    onClick,
    mostPopular,
    active,
  }: {
    children: React.ReactNode;
    onClick: () => void;
    mostPopular?: boolean;
    active?: boolean;
  }) => (
    <button
      className={cn(
        "rounded-[44px] text-black w-full h-[32px] relative font-medium text-lg",
        active ? "bg-white" : "bg-white/50",
      )}
      onClick={onClick}
    >
      {children}
      {mostPopular && (
        <div
          className={cn(
            "absolute -top-1.5 right-1 rounded-full bg-primary-blue p-[1px]",
            !active && "brightness-75",
          )}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M8.00033 0.666992L9.64676 5.66989H14.9747L10.6643 8.76186L12.3108 13.7648L8.00033 10.6728L3.6899 13.7648L5.33634 8.76186L1.02591 5.66989H6.35389L8.00033 0.666992Z"
              fill="white"
            />
          </svg>
        </div>
      )}
    </button>
  );

  let hugeButtonTitle: string;
  if (selectedSubscription === "Pro") {
    if (hasSub) {
      hugeButtonTitle = t("legacy.managePlan");
    } else {
      hugeButtonTitle = t("legacy.getPro");
    }
  } else if (selectedSubscription === "Elite") {
    if (hasProSub) {
      hugeButtonTitle = t("legacy.upgradePlan");
    } else if (hasEliteSub || hasUltimateSub) {
      hugeButtonTitle = t("legacy.managePlan");
    } else {
      hugeButtonTitle = t("legacy.getElite");
    }
  } else if (selectedSubscription === "Ultimate") {
    if (hasEliteSub || hasProSub) {
      hugeButtonTitle = t("legacy.upgradePlan");
    } else if (hasUltimateSub) {
      hugeButtonTitle = t("legacy.managePlan");
    } else {
      hugeButtonTitle = t("legacy.getUltimate");
    }
  }

  const bottomPart = (
    <div className="flex flex-col items-center text-white gap-y-5">
      {isMobile && (
        <div className="flex flex-row w-[308px] justify-between gap-x-2">
          <MobileSelectSubscriptionButton
            onClick={() => handleSubscriptionClicked("Pro")}
            active={selectedSubscription === "Pro"}
          >
            {t("tiers.pro")}
          </MobileSelectSubscriptionButton>
          <MobileSelectSubscriptionButton
            onClick={() => handleSubscriptionClicked("Ultimate")}
            active={selectedSubscription === "Ultimate"}
            mostPopular={true}
          >
            {t("tiers.ultimate")}
          </MobileSelectSubscriptionButton>
          <MobileSelectSubscriptionButton
            onClick={() => handleSubscriptionClicked("Elite")}
            active={selectedSubscription === "Elite"}
          >
            {t("tiers.elite")}
          </MobileSelectSubscriptionButton>
        </div>
      )}
      <HugeButton>
        {hugeButtonTitle}
        {loading && (
          <Oval
            width={20}
            height={20}
            className="absolute right-3 top-1/2 -translate-y-1/2"
          />
        )}
      </HugeButton>
      <p className="max-w-[570px] text-center text-dark-tertiary text-sm">
        {t("legacy.continues")}
      </p>
    </div>
  );

  return (
    <main className="flex flex-col items-center gap-y-6">
      <Helmet>
        <title>{t("pageTitle", { game: GAME_NAME })}</title>
        <meta name="description" content={t("metaDescription")} />
        <link rel="canonical" href={rootCanonicalUrl("/subscription")} />
        {alternateLinks(rootCanonicalUrl("/subscription"))}
      </Helmet>

      <>
        <div className="absolute right-0 left-0 bg-linear-to-b to-[#141414] from-[#1C2A37] bottom-0 -z-10 touch-device:top-[50px]" />
        <h1 className="text-white text-[24px] touch-device:text-[36px] pt-4 touch-device:pt-16 text-center px-4 touch-device:pb-8 hidden md-lg:flex">
          {t("legacy.headline")}
        </h1>
        <div className="flex flex-row mt-4 text-sm text-white md-lg:mb-2 md-lg:mt-0 gap-x-3 touch-device:text-base large-2:text-lg large-2:pb-2">
          <div className="flex flex-col gap-y-1.5">
            <div className="flex mx-auto gap-x-2">
              <p
                className={cn(
                  yearlySubActive && "text-gray-300",
                  "font-normal",
                )}
              >
                {t("billing.monthly")}
              </p>
              <div className="my-auto">
                <Switch
                  onChange={() => setYearlySubActive((prev) => !prev)}
                  isActive={yearlySubActive}
                />
              </div>
              <p
                className={cn(
                  !yearlySubActive && "text-gray-300",
                  "font-semibold",
                )}
              >
                {t("billing.yearly")}
              </p>
            </div>
            <p className="my-auto text-lg font-light desktop:text-xl touch-device:text-2xl text-white/50">
              {t("legacy.yearlyOffer")}
            </p>
          </div>
        </div>
      </>

      {subscriptions}
      {bottomPart}
      {hasActiveSub ? (
        <div ref={clubImportRef}>
          <PremiumFeatures />
        </div>
      ) : (
        <div ref={creditsRef} className="w-full">
          <CreditsExplainer
            onSeePlans={() =>
              plansRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
              })
            }
          />
        </div>
      )}
    </main>
  );
};

export default LegacySubscriptionPage;
